// v7 CameraTile — the reusable camera-tile primitive used by the
// /dashboards wall, the view-mode tile modal, and (wave 4) the library
// row preview. Anatomy mirrors mockups/cameras-dashboard-v7.html
// (.cam-tile): wrapper → feed → scanlines → vignette → corner overlays
// (tl / tr / bl) → loading shimmer → edit-mode chrome (remove + 4
// resize handles).
//
// This wave does NOT wire WHEP/snapshots. Each tile renders a stable
// gradient feed derived from a hash of `doorbot_id` so a wall of tiles
// reads as 16 distinct cameras (the mockup hardcodes feed-1..feed-16
// for the same effect). Wave 4+ will swap the gradient for a real
// snapshot or live frame; the rest of the anatomy stays put.
//
// Props are deliberately spare — the wall + modal both build the same
// element with different `mode`, `chip`, and `onRemove` callbacks. The
// `chip` slot is a single corner badge (Motion / Person / Low bitrate
// / Offline) so callers don't have to know about .cam-tile__chip
// classes.

import { useRef, type MouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import { feedGradientFor } from "./cameraTileFeed";
import { useSnapshotPoll } from "./useSnapshotPoll";
import { useViewportPriority } from "./viewportPriority";
import { LiveTileVideo } from "./LiveTileVideo";

/** Which corner the operator grabbed when starting a resize. */
export type ResizeCorner = "tl" | "tr" | "bl" | "br";

/** Top-right status pill content. Tone maps to the v7 chip variants. */
export interface CameraTileChip {
  tone: "info" | "warn" | "danger" | "neutral";
  label: string;
}

export interface CameraTileCamera {
  /** Wire ID — used as the deterministic seed for the feed gradient. */
  doorbot_id: string;
  /** Display name, top-left + bottom-left of the tile. */
  name: string;
  /** Bottom-left subline. Mockup shows e.g. "ZAS6 · Austin, TX". */
  location_name?: string | null;
}

export interface CameraTileProps {
  camera: CameraTileCamera;
  /** When true, render the shimmer + "Reconnecting" pill. */
  isLoading?: boolean;
  /**
   * When true, dim/desaturate the feed and drop a static "Offline"
   * label. Distinct from isLoading — offline tiles never spin.
   */
  isOffline?: boolean;
  /** Top-right corner badge. Omit for no chip. */
  chip?: CameraTileChip;
  /**
   * Top-left freshness label (e.g. "12s", "1m"). When provided we also
   * show the LIVE pill next to it, mirroring the mockup. Omit on the
   * library row preview where freshness isn't relevant.
   */
  age?: string;
  /**
   * "view"  → renders the LIVE/age pill in TR slot, no remove button,
   *           no resize handles. Click target.
   * "edit"  → hides the chip slot, renders remove button + 4 corner
   *           resize handles. Wave 4 will wire pointer events; this
   *           wave only surfaces the affordances visually.
   */
  mode?: "view" | "edit";
  /** Sizing variant — affects label size only (mostly used by the modal hero). */
  size?: "sm" | "md" | "lg";
  /**
   * "static" tiles drop the lift/shadow hover transition (used inside
   * the modal hero where the tile already fills the surface).
   */
  staticTile?: boolean;
  /** Called when the remove button is clicked (edit mode only). */
  onRemove?: () => void;
  /** Called when the tile body is clicked (view mode → opens modal). */
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  /**
   * Edit mode: pointerdown on the tile body — used by the wall page to
   * start a drag-to-reorder gesture. The wall owns the move/up listeners.
   */
  onBodyPointerDown?: (e: ReactPointerEvent<HTMLDivElement>) => void;
  /**
   * Edit mode: pointerdown on a corner resize handle. The wall owns the
   * move/up listeners; this just signals which corner was grabbed.
   */
  onResizePointerDown?: (
    corner: ResizeCorner,
    e: ReactPointerEvent<HTMLSpanElement>,
  ) => void;
  /** Forwarded for selection state in the library pane (wave 4). */
  isSelected?: boolean;
  /** Render as a drop ghost during a drag (dotted halo, opacity dim). */
  isGhost?: boolean;
  /** Optional aria-label override; defaults to "{name}". */
  ariaLabel?: string;
}

const CHIP_TONE_CLASS: Record<CameraTileChip["tone"], string> = {
  info: "v7-cam-tile__chip v7-cam-tile__chip--info",
  warn: "v7-cam-tile__chip v7-cam-tile__chip--warn",
  danger: "v7-cam-tile__chip v7-cam-tile__chip--danger",
  neutral: "v7-cam-tile__chip",
};

export function CameraTile({
  camera,
  isLoading = false,
  isOffline = false,
  chip,
  age,
  mode = "view",
  size = "md",
  staticTile = false,
  onRemove,
  onClick,
  onBodyPointerDown,
  onResizePointerDown,
  isSelected = false,
  isGhost = false,
  ariaLabel,
}: CameraTileProps): JSX.Element {
  const stateClasses = [
    "v7-cam-tile",
    `v7-cam-tile--${size}`,
    isLoading ? "is-loading" : "",
    isOffline ? "is-offline" : "",
    isSelected ? "is-selected" : "",
    isGhost ? "is-ghost" : "",
    mode === "edit" ? "is-edit" : "",
    staticTile ? "v7-cam-tile--static" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Snapshot wiring — same primitive the legacy /cameras wall uses.
  // Disabled for offline cameras (avoids guaranteed-404 round trips) and
  // for the modal hero (which uses the wall's already-fresh image via
  // `staticTile` + the parent rendering its own poller). Audio is N/A
  // here; snapshots are still images.
  const tileRootRef = useRef<HTMLDivElement | null>(null);
  const { priority, priorityRef } = useViewportPriority(tileRootRef, {
    enabled: !isOffline && !staticTile,
  });
  const { src: snapshotSrc } = useSnapshotPoll({
    deviceId: camera.doorbot_id,
    enabled: !isOffline && !staticTile,
    priority,
    priorityRef,
  });

  const handleRemove = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onRemove?.();
  };

  const showChip = mode === "view" && !isLoading && chip !== undefined;
  const showAge = mode === "view" && !isLoading && !isOffline && age !== undefined;

  return (
    <div
      ref={tileRootRef}
      className={stateClasses}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel ?? camera.name}
      onClick={onClick}
      onPointerDown={onBodyPointerDown}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          // Cast keyboard event up to MouseEvent shape — the consumer
          // only ever uses currentTarget for positioning.
          onClick(e as unknown as MouseEvent<HTMLDivElement>);
        }
      }}
    >
      {/* Feed background — stable per-camera gradient as the placeholder
          beneath the snapshot. Renders even before the first poll lands. */}
      <div
        className="v7-cam-tile__feed"
        style={{ background: feedGradientFor(camera.doorbot_id) }}
        aria-hidden="true"
      />
      {/* Snapshot underlay — fades in over the gradient when the poll
          lands and stays as the placeholder beneath the WHEP video,
          visible while live is connecting / reconnecting / off-viewport. */}
      {snapshotSrc !== null && (
        <img
          className="v7-cam-tile__snapshot"
          src={snapshotSrc}
          alt={`Snapshot from ${camera.name}`}
          referrerPolicy="no-referrer"
          decoding="async"
          loading="lazy"
        />
      )}
      {/* Live WHEP video — only opens when this tile is visible and the
          tab is foregrounded. Audio is hard-disabled (muted; no audio
          transceiver added). The snapshot stays underneath and shows
          through any gap in the live stream. */}
      <LiveTileVideo
        deviceId={camera.doorbot_id}
        enabled={mode === "view" && !isOffline && !staticTile}
        priority={priority}
        rootRef={tileRootRef}
      />
      <div className="v7-cam-tile__scanlines" aria-hidden="true" />
      <div className="v7-cam-tile__vignette" aria-hidden="true" />

      {/* Loading overlay: shimmer + spinner pill. Suppresses corner badges
          so the operator's eye lands on the reconnecting state. */}
      {isLoading && (
        <>
          <div className="v7-cam-tile__shimmer" aria-hidden="true" />
          <span className="v7-cam-tile__loading-label" role="status">
            Reconnecting
          </span>
        </>
      )}

      {/* Offline overlay: static "Offline" pill, no spinner. */}
      {isOffline && !isLoading && (
        <span
          className="v7-cam-tile__loading-label"
          style={{ background: "rgba(0,0,0,0.7)" }}
          role="status"
        >
          Offline
        </span>
      )}

      {/* Top-left: LIVE dot + age pill. Hidden in edit mode (the chrome
          competes with resize handles for that corner space). */}
      {mode === "view" && showAge && (
        <div className="v7-cam-tile__tl">
          <span className="v7-cam-tile__live">
            <i aria-hidden="true" />
            Live
          </span>
          <span className="v7-cam-tile__age" title={`Last update ${age}`}>
            {age}
          </span>
        </div>
      )}

      {/* Top-right: chip slot (view mode only). */}
      {showChip && (
        <div className="v7-cam-tile__tr">
          <span className={CHIP_TONE_CLASS[chip.tone]}>{chip.label}</span>
        </div>
      )}

      {/* Bottom-left: name + location, gradient-veiled. */}
      <div className="v7-cam-tile__bl">
        <span className="v7-cam-tile__name">{camera.name}</span>
        {camera.location_name && (
          <span className="v7-cam-tile__loc">{camera.location_name}</span>
        )}
      </div>

      {/* Edit-mode chrome: remove button + 4 resize handles. The
          handles are visually present but pointer wiring lands in
          wave 4 (drag/resize). The remove button is fully wired now
          since v7's remove is just "drop the tile from the array",
          which the page can do without dragging. */}
      {mode === "edit" && (
        <>
          <button
            type="button"
            className="v7-tile-remove"
            title="Remove from dashboard"
            aria-label={`Remove ${camera.name} from dashboard`}
            onClick={handleRemove}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19 6.4L17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z" />
            </svg>
          </button>
          <span
            className="v7-resize-handle v7-resize-handle--tl"
            aria-hidden="true"
            onPointerDown={
              onResizePointerDown
                ? (e) => {
                    e.stopPropagation();
                    onResizePointerDown("tl", e);
                  }
                : undefined
            }
          />
          <span
            className="v7-resize-handle v7-resize-handle--tr"
            aria-hidden="true"
            onPointerDown={
              onResizePointerDown
                ? (e) => {
                    e.stopPropagation();
                    onResizePointerDown("tr", e);
                  }
                : undefined
            }
          />
          <span
            className="v7-resize-handle v7-resize-handle--bl"
            aria-hidden="true"
            onPointerDown={
              onResizePointerDown
                ? (e) => {
                    e.stopPropagation();
                    onResizePointerDown("bl", e);
                  }
                : undefined
            }
          />
          <span
            className="v7-resize-handle v7-resize-handle--br"
            aria-hidden="true"
            onPointerDown={
              onResizePointerDown
                ? (e) => {
                    e.stopPropagation();
                    onResizePointerDown("br", e);
                  }
                : undefined
            }
          />
        </>
      )}
    </div>
  );
}
